import { useEffect, useRef, useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import pageFrameCopy from '@/i18n/ns/page-frame';
import { apiUrl } from '@/lib/shell';
import { workspaceApi } from '../api';
import { useWorkspaceText } from '../interface-copy';
import type { CompanyResult } from '../types';
import { PageFrame } from './page-frame';
import { readPagePayload } from './payload';

type LoadAddress = (id: string) => Promise<string>;

/** A saved Page's signed address for the revision shown, fetched again when a revision moves it. */
function usePageAddress(id: string, revision: number | undefined, load: LoadAddress) {
  const [address, setAddress] = useState<{ key: string; path: string | null } | null>(null);
  const key = `${id}:${revision ?? 0}`;
  const loader = useRef(load);
  loader.current = load;
  useEffect(() => {
    let current = true;
    loader.current(id).then(
      (path) => current && setAddress({ key, path }),
      () => current && setAddress({ key, path: null }),
    );
    return () => {
      current = false;
    };
  }, [id, key]);
  return address?.key === key ? address : null;
}

/** A Page (a Result of kind html) in the Result canvas: the running page, and what did not carry over in words. */
export function ResultPage({
  result,
  full,
  origin = window.location.origin,
  onOpen = (url) => void window.open(url, '_blank', 'noopener'),
  loadAddress = workspaceApi.pageAddress,
}: {
  result: Pick<CompanyResult, 'id' | 'title' | 'payload' | 'currentRevision'>;
  full: boolean;
  origin?: string;
  onOpen?: (url: string) => void;
  /** The signed address of the page (GET /api/v1/company/results/:id/page). */
  loadAddress?: LoadAddress;
}) {
  const { text: uiText } = useWorkspaceText();
  const copy = useNamespaceCopy(pageFrameCopy);
  const page = readPagePayload(result.payload);
  // Poneva's own page runs no inline script, and a srcdoc frame would inherit that policy: the
  // canvas frames the page's signed address, served with the page's own policy.
  const address = usePageAddress(result.id, result.currentRevision, loadAddress);
  if (!page || address?.path === null)
    return <p className="ws-muted">{uiText('No preview is available for this result.')}</p>;
  return (
    <>
      {address?.path ? (
        <PageFrame
          html={page.html}
          resources={page.resources}
          origin={origin}
          // In the native shells the page is Poneva's, not the app bundle's own origin.
          src={apiUrl(address.path)}
          title={result.title}
          fill={full}
          onOpen={onOpen}
        />
      ) : (
        <p className="ws-muted" aria-busy="true" />
      )}
      {page.noteCount > 0 && (
        <p className="ws-page-note ws-page-notes">
          <strong>{copy.pageNotesTitle}</strong>
          {formatCopy(copy.pageNotesBody, { count: page.noteCount })}
        </p>
      )}
    </>
  );
}
